/**
 * AuditExportsPage
 *
 * Admin page for managing secondary audit log sinks (Pub/Sub, BigQuery, GCS).
 * Lists existing sinks, allows toggling, testing, deleting, flushing GCS buffers,
 * and adding new sinks.
 *
 * Route: /settings/audit  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  Database,
  Trash2,
  CheckCircle,
  AlertCircle,

  RefreshCw,
  FlaskConical,
} from "lucide-react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import Loader from "../../components/Loader/Loader";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import EmptyState from "../../components/shared/EmptyState";
import {
  listAuditSinks,
  updateAuditSink,
  deleteAuditSink,
  testAuditSink,
  flushGCSSink,
} from "../../api/adminAuditSinks";
import AuditSinkForm, { SINK_LABELS } from "../../components/Settings/AuditSinkForm";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../router/routes";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SinkBadge = ({ type }) => {
  const colours = {
    pubsub:   "bg-fill-blue-100 text-ink-blue-700 dark:bg-fill-blue-700/20 dark:text-ink-blue-300",
    bigquery: "bg-fill-green-100 text-ink-green-700 dark:bg-fill-green-700/20 dark:text-ink-green-300",
    gcs:      "bg-fill-hex-f3e8ff text-ink-hex-7e22ce dark:bg-fill-hex-7e22ce/20 dark:text-ink-hex-d8b4fe",
  };
  return (
    <span
      className={`photon-metadata-label inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colours[type] ?? "bg-fill-gray-100 text-ink-gray-700 dark:bg-fill-gray-700 dark:text-ink-gray-300"}`}
    >
      {SINK_LABELS[type] ?? type}
    </span>
  );
};

// ─── Sink Row ─────────────────────────────────────────────────────────────────────────────

const SinkRow = ({ sink, onToggle, onDelete, onTest, onFlush }) => {
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [flushing, setFlushing] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    await onToggle(sink.sink_id, !sink.enabled);
    setToggling(false);
  };

  const handleTest = async () => {
    setTesting(true);
    await onTest(sink.sink_id);
    setTesting(false);
  };

  const handleFlush = async () => {
    setFlushing(true);
    await onFlush(sink.sink_id);
    setFlushing(false);
  };

  return (
    <div className="photon-content-card flex flex-col gap-2 rounded-xl border border-edge-gray-200 dark:border-edge-gray-700 bg-surface-card-slate dark:bg-surface-card-slate p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <SinkBadge type={sink.sink_type} />
            {sink.enabled ? (
              <span className="text-xs text-ink-green-600 dark:text-ink-green-400 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Active
              </span>
            ) : (
              <span className="text-xs text-ink-gray-400">Disabled</span>
            )}
          </div>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 font-mono break-all">
            {sink.sink_id}
          </p>
          {sink.last_error && (
            <p className="text-xs text-ink-red-500 flex items-center gap-1 mt-0.5">
              <AlertCircle className="w-3 h-3" />
              {sink.last_error}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Toggle */}
          <button
            onClick={handleToggle}
            disabled={toggling}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${
              sink.enabled
                ? "bg-fill-gray-100 dark:bg-fill-gray-700 text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-200"
                : "bg-fill-emerald-50 dark:bg-fill-emerald-900/40 text-ink-green-700 dark:text-ink-green-400 hover:bg-fill-emerald-100"
            }`}
          >
            {toggling ? "…" : sink.enabled ? "Disable" : "Enable"}
          </button>

          {/* Test */}
          <button
            onClick={handleTest}
            disabled={testing}
            title="Test IAM connection"
            className="text-xs px-3 py-1.5 rounded-lg font-medium bg-fill-gray-100 dark:bg-fill-gray-700 text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <FlaskConical className="w-3 h-3" />
            {testing ? "Testing…" : "Test"}
          </button>

          {/* GCS flush */}
          {sink.sink_type === "gcs" && (
            <button
              onClick={handleFlush}
              disabled={flushing}
              title="Flush GCS buffer now"
              className="text-xs px-3 py-1.5 rounded-lg font-medium bg-fill-gray-100 dark:bg-fill-gray-700 text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              {flushing ? "Flushing…" : "Flush"}
            </button>
          )}

          {/* Delete */}
          <button
            onClick={() => onDelete(sink)}
            title="Delete sink"
            className="p-1.5 rounded-lg text-ink-gray-400 hover:text-ink-red-600 hover:bg-fill-red-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Config summary */}
      <div className="mt-1 rounded-lg bg-surface-code-slate dark:bg-surface-code-slate p-2.5">
        <pre className="text-xs text-ink-gray-500 dark:text-ink-gray-400 whitespace-pre-wrap break-all">
          {JSON.stringify(sink.config, null, 2)}
        </pre>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const AuditExportsPage = () => {
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const [sinks, setSinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAuditSinks();
      setSinks(data);
    } catch (err) {
      if (err instanceof AuthError) {
        UIStore.setAuthError(true);
        navigate(ROUTES.LOGIN);
        return;
      }
      showFeedback("Could not load audit sinks.", "error");
    } finally {
      setLoading(false);
    }
  }, [navigate, showFeedback]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (sinkId, enabled) => {
    try {
      await updateAuditSink(sinkId, enabled);
      setSinks((prev) =>
        prev.map((s) => (s.sink_id === sinkId ? { ...s, enabled } : s))
      );
    } catch {
      showFeedback("Failed to update sink.", "error");
    }
  };

  const handleTest = async (sinkId) => {
    try {
      await testAuditSink(sinkId);
      showFeedback("Connection test passed.", "success");
      // clear last_error on success
      setSinks((prev) =>
        prev.map((s) => (s.sink_id === sinkId ? { ...s, last_error: null } : s))
      );
    } catch (err) {
      showFeedback(err?.response?.data?.error ?? "Connection test failed.", "error");
    }
  };

  const handleFlush = async (sinkId) => {
    try {
      const count = await flushGCSSink(sinkId);
      showFeedback(`Flushed ${count} event${count !== 1 ? "s" : ""} to GCS.`, "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error ?? "Flush failed.", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAuditSink(deleteTarget.sink_id);
      setSinks((prev) => prev.filter((s) => s.sink_id !== deleteTarget.sink_id));
      showFeedback("Audit sink deleted.", "success");
      setDeleteTarget(null);
    } catch {
      showFeedback("Failed to delete sink.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SettingsPageShell
      title="Audit"
      description="Forward audit events to secondary destinations for compliance, SIEM integration, or long-term archival. Events are always written to Photon's Firestore audit log first."
      action={!loading && sinks.length > 0 ? { label: "Add Sink", onClick: () => setShowAddForm((v) => !v) } : undefined}
    >
      <div className="flex flex-col gap-6 max-w-3xl">
        {/* Inline add form */}
        <AnimatePresence>
          {showAddForm && (
            <AuditSinkForm
              onCancel={() => setShowAddForm(false)}
              onCreated={() => { setShowAddForm(false); load(); }}
            />
          )}
        </AnimatePresence>

        {/* Sink list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : sinks.length === 0 && !showAddForm ? (
          <EmptyState
            icon={<Database className="w-7 h-7 text-ink-primary dark:text-ink-accent" />}
            title="No audit sinks configured"
            description="Forward audit events to Pub/Sub, BigQuery, or Cloud Storage for compliance and archival."
            action={{ label: "Add Sink", onClick: () => setShowAddForm(true) }}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {sinks.map((sink) => (
              <SinkRow
                key={sink.sink_id}
                sink={sink}
                onToggle={handleToggle}
                onDelete={setDeleteTarget}
                onTest={handleTest}
                onFlush={handleFlush}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Audit Sink"
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      >
        Remove the <strong>{SINK_LABELS[deleteTarget?.sink_type]}</strong> sink? Audit events will no
        longer be forwarded to this destination. This cannot be undone.
      </ConfirmDialog>
    </SettingsPageShell>
  );
};

export default AuditExportsPage;
